import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Checkbox, Input, Modal, Radio, Segmented, Space, Tag, Typography, message,
} from 'antd';
import api, { getStaticImageUrl } from '../services/api';
import { TEST_DIFFICULTY_OPTIONS } from '../constants/testDifficulty';
import { getApiErrorMessage } from '../utils/apiError';
import QuestionImageField from './QuestionImageField';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

/**
 * Правка вопроса прямо из карточки теста.
 *
 * Вопрос может лежать копиями в десятках тестов, поэтому сохранение — это
 * всегда выбор охвата. Backend отвечает 409 с перечнем затронутых копий,
 * и только после явного выбора правка применяется. Копии, у которых
 * совпадает структура, но расходится отметка правильного ответа, требуют
 * отдельного согласия: это исправление ошибки, а не обычная правка.
 */

export interface EditableAnswer {
  id?: number;
  text: string;
  is_correct: boolean;
  order: number;
  matching_key?: string | null;
  image_url?: string | null;
}

export interface EditableQuestion {
  id: number;
  text: string;
  type: string;
  order: number;
  difficulty?: string | null;
  points: number;
  image_url?: string | null;
  explanation?: string | null;
  explanation_image_urls?: string[] | null;
  explanation_image_captions?: string[] | null;
  answers: EditableAnswer[];
}

type AnswerDraft = EditableAnswer & { editorKey: number };

interface ScopeInfo {
  duplicate_questions_count: number;
  duplicate_tests_count: number;
  resolvable_conflict_questions_count?: number;
  resolvable_conflict_tests_count?: number;
  excluded_conflicting_questions_count?: number;
  confirmation_token: string;
}

interface Props {
  question: EditableQuestion | null;
  onClose: () => void;
  onSaved: () => void;
}

const QuestionInlineEditor: React.FC<Props> = ({ question, onClose, onSaved }) => {
  const [text, setText] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [questionType, setQuestionType] = useState('single_choice');
  const [imageUrl, setImageUrl] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [answerUploads, setAnswerUploads] = useState<Set<number>>(() => new Set());
  const [answers, setAnswers] = useState<AnswerDraft[]>([]);
  const nextAnswerKey = useRef(0);
  const [saving, setSaving] = useState(false);
  const [scopeInfo, setScopeInfo] = useState<ScopeInfo | null>(null);
  const [scope, setScope] = useState<'single' | 'duplicates'>('single');
  const [resolveConflicts, setResolveConflicts] = useState(false);

  useEffect(() => {
    setImageUploading(false);
    setAnswerUploads(new Set());
    if (!question) return;
    setText(question.text);
    setDifficulty(question.difficulty ?? '');
    setQuestionType(question.type);
    setImageUrl(question.image_url || '');
    setAnswers(question.answers.map((a) => ({ ...a, editorKey: nextAnswerKey.current++ })));
    setScopeInfo(null);
    setScope('single');
    setResolveConflicts(false);
  }, [question]);

  if (!question) return null;

  const multiple = questionType === 'multiple_choice';
  const choiceQuestion = multiple || questionType === 'single_choice';
  const correctAnswerCount = answers.filter((answer) => answer.is_correct).length;
  const selectionError = questionType === 'single_choice' && correctAnswerCount !== 1
    ? 'Для режима «Один верный» выберите ровно один правильный ответ.'
    : multiple && correctAnswerCount === 0
      ? 'Отметьте хотя бы один правильный ответ.'
      : null;

  const emptyAnswerIndex = answers.findIndex((answer) => !answer.text.trim());
  const contentError = !text.trim()
    ? 'Введите формулировку вопроса.'
    : choiceQuestion && answers.length < 2
      ? 'Добавьте минимум два варианта ответа.'
    : emptyAnswerIndex >= 0
      ? `Заполните вариант ответа ${emptyAnswerIndex + 1}.`
      : null;
  const validationError = contentError || selectionError;
  const uploadsPending = imageUploading || answerUploads.size > 0;

  const payload = (extra: Record<string, unknown> = {}) => ({
    text,
    type: questionType,
    order: question.order,
    difficulty: difficulty || null,
    points: question.points,
    image_url: imageUrl.trim() || null,
    explanation: question.explanation,
    explanation_image_urls: question.explanation_image_urls,
    explanation_image_captions: question.explanation_image_captions,
    answers: answers.map((a) => ({
      id: a.id ?? null,
      text: a.text,
      is_correct: a.is_correct,
      order: a.order,
      matching_key: a.matching_key ?? null,
      image_url: a.image_url?.trim() || null,
    })),
    ...extra,
  });

  const save = async (extra: Record<string, unknown> = {}) => {
    if (validationError || uploadsPending) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/questions/${question.id}`, payload(extra));
      const propagated = data?.propagated_questions || 0;
      const resolved = data?.resolved_conflict_questions || 0;
      message.success(
        resolved ? `Сохранено, выровнено копий: ${propagated + resolved}`
          : propagated ? `Сохранено, обновлено копий: ${propagated}`
            : 'Сохранено',
      );
      onSaved();
      onClose();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409
        && detail?.code === 'duplicate_question_update_confirmation_required') {
        // Вопрос живёт в других тестах — спрашиваем охват, ничего не меняя.
        setScopeInfo(detail as ScopeInfo);
      } else {
        message.error(getApiErrorMessage(error, 'не удалось сохранить'));
      }
    } finally {
      setSaving(false);
    }
  };

  const addAnswer = () => {
    const editorKey = nextAnswerKey.current++;
    setAnswers((prev) => [...prev, {
      editorKey, text: '', is_correct: false, order: prev.length,
    }].map((answer, order) => ({ ...answer, order })));
  };

  const removeAnswer = (editorKey: number) => {
    setAnswers((prev) => prev.filter((answer) => answer.editorKey !== editorKey)
      .map((answer, order) => ({ ...answer, order })));
  };

  const setAnswerImage = (editorKey: number, value: string) => {
    setAnswers((prev) => prev.map((answer) => answer.editorKey === editorKey
      ? { ...answer, image_url: value || null } : answer));
  };

  const setAnswerUploading = (editorKey: number, uploading: boolean) => {
    setAnswerUploads((prev) => {
      if (prev.has(editorKey) === uploading) return prev;
      const next = new Set(prev);
      if (uploading) next.add(editorKey);
      else next.delete(editorKey);
      return next;
    });
  };

  const setCorrect = (index: number, value: boolean) => {
    setAnswers((prev) => prev.map((a, i) => {
      if (multiple) return i === index ? { ...a, is_correct: value } : a;
      return { ...a, is_correct: i === index };
    }));
  };

  if (scopeInfo) {
    const conflicts = scopeInfo.resolvable_conflict_questions_count || 0;
    return (
      <Modal
        open title="Где применить правку" onCancel={() => setScopeInfo(null)}
        okText={resolveConflicts ? 'Применить и выровнять' : 'Применить'}
        okButtonProps={{ loading: saving, danger: resolveConflicts, disabled: !!validationError || uploadsPending }}
        onOk={() => save({
          update_scope: scope,
          confirmation_token: scopeInfo.confirmation_token,
          resolve_answer_conflicts: resolveConflicts,
        })}
        width={620}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Этот вопрос есть и в других тестах. Выберите, что изменить.
          </Paragraph>
          <Radio.Group value={scope} onChange={(e) => setScope(e.target.value)}>
            <Space direction="vertical" size={8}>
              <Radio value="single">
                <b>Только в этом тесте</b>
                <div><Text type="secondary">
                  Остальные {scopeInfo.duplicate_questions_count} копий останутся прежними.
                </Text></div>
              </Radio>
              <Radio value="duplicates">
                <b>Во всех точных копиях</b>
                <div><Text type="secondary">
                  Затронет {scopeInfo.duplicate_questions_count} копий
                  в {scopeInfo.duplicate_tests_count} тестах. Завершённые попытки
                  будут пересчитаны — баллы у части сотрудников изменятся.
                </Text></div>
              </Radio>
            </Space>
          </Radio.Group>

          {conflicts > 0 && scope === 'duplicates' && (
            <>
              <Checkbox
                checked={resolveConflicts}
                onChange={(e) => setResolveConflicts(e.target.checked)}
              >
                Выровнять и копии с расхождением
              </Checkbox>
              <Alert
                type="warning" showIcon
                message={`Ещё у ${conflicts} копий в ${scopeInfo.resolvable_conflict_tests_count} тестах совпадают все варианты, но верным отмечено другое. Без этой галочки они останутся как есть.`}
              />
            </>
          )}

          {!!scopeInfo.excluded_conflicting_questions_count && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Ещё {scopeInfo.excluded_conflicting_questions_count} вопросов с тем же
              текстом, но другим набором вариантов, не затрагиваются ни при одном
              охвате — это другие вопросы.
            </Text>
          )}
        </Space>
      </Modal>
    );
  }

  return (
    <Modal
      open title="Правка вопроса" onCancel={onClose}
      okText="Сохранить" onOk={() => save()} okButtonProps={{ loading: saving, disabled: !!validationError || uploadsPending }}
      width={720}
    >
      <Space direction="vertical" size={14} style={{ width: '100%' }}>
        <div>
          <Text type="secondary">Формулировка</Text>
          <TextArea
            value={text} onChange={(e) => setText(e.target.value)}
            autoSize={{ minRows: 2, maxRows: 6 }}
          />
        </div>

        <div>
          <label htmlFor="question-inline-difficulty">Сложность вопроса</label>
          <select id="question-inline-difficulty" className="tw-field" value={difficulty} onChange={(event) => setDifficulty(event.target.value)}>
            <option value="">Не задана</option>
            {TEST_DIFFICULTY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
        <QuestionImageField
          key={question.id}
          id="question-inline-image"
          value={imageUrl}
          onChange={setImageUrl}
          onUploadingChange={setImageUploading}
        />

        <div>
          <Space direction="vertical" size={6} style={{ marginBottom: 8, width: '100%' }}>
            <Text type="secondary">Варианты ответа</Text>
            {choiceQuestion ? (
              <Segmented
                aria-label="Режим выбора ответа"
                value={questionType}
                options={[
                  { label: 'Один верный', value: 'single_choice' },
                  { label: 'Несколько верных', value: 'multiple_choice' },
                ]}
                onChange={(value) => setQuestionType(String(value))}
              />
            ) : <Tag>один верный</Tag>}
          </Space>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {answers.map((answer, index) => (
              <div key={answer.editorKey} className="question-inline-editor-answer" style={{ width: '100%' }}>
              <Space.Compact style={{ width: '100%' }}>
                {multiple ? (
                  <Checkbox
                    checked={answer.is_correct}
                    onChange={(e) => setCorrect(index, e.target.checked)}
                    style={{ marginRight: 8 }}
                  />
                ) : (
                  <Radio
                    checked={answer.is_correct}
                    onChange={() => setCorrect(index, true)}
                    onClick={() => {
                      if (correctAnswerCount !== 1) setCorrect(index, true);
                    }}
                    style={{ marginRight: 8 }}
                  />
                )}
                <Input
                  value={answer.text}
                  style={{ flex: 1, minWidth: 0 }}
                  aria-label={`Вариант ответа ${index + 1}`}
                  status={!answer.text.trim() ? 'error' : undefined}
                  onChange={(event) => {
                    // Read before React restores the controlled input or runs this updater later.
                    const value = event.target.value;
                    setAnswers((prev) => prev.map((a, i) =>
                      (i === index ? { ...a, text: value } : a)));
                  }}
                />
                {choiceQuestion && (
                  <Button danger disabled={saving}
                    aria-label={`Убрать вариант ответа ${index + 1}`}
                    onClick={() => removeAnswer(answer.editorKey)}>
                    Убрать
                  </Button>
                )}
              </Space.Compact>
              {!answer.text.trim() && (
                <div role="alert" style={{ marginLeft: 24 }}>
                  <Text type="danger">Введите текст варианта ответа {index + 1}.</Text>
                </div>
              )}
              <div style={{ marginTop: 6, marginLeft: 24 }}>
                <QuestionImageField
                  key={`${question.id}-${answer.editorKey}`}
                  id={`question-inline-answer-image-${answer.editorKey}`}
                  value={answer.image_url || ''}
                  onChange={(value) => setAnswerImage(answer.editorKey, value)}
                  onUploadingChange={(uploading) => setAnswerUploading(answer.editorKey, uploading)}
                  label={`Фото варианта ответа ${index + 1} (необязательно)`}
                  imageAlt={`Вариант ответа ${index + 1}`}
                  fileLabel={`Файл изображения варианта ответа ${index + 1}`}
                  previewClassName="question-inline-editor-answer-image"
                  compact
                />
                {answer.image_url?.trim() && (
                  <div>
                    <Typography.Link href={getStaticImageUrl(answer.image_url)} target="_blank" rel="noopener noreferrer">
                      Открыть изображение варианта {index + 1}
                    </Typography.Link>
                  </div>
                )}
              </div>
              </div>
            ))}
          </Space>
          {choiceQuestion && (
            <Button type="dashed" block disabled={saving} onClick={addAnswer} style={{ marginTop: 10 }}>
              Добавить вариант ответа
            </Button>
          )}
        </div>

        {validationError && <Alert type="warning" showIcon message={validationError} />}

        <Alert
          type="info" showIcon
          message="Если этот вопрос есть в других тестах, перед сохранением будет предложено выбрать охват правки."
        />
      </Space>
    </Modal>
  );
};

export default QuestionInlineEditor;
